import React, { useState, useCallback, useEffect, useRef } from "react";
import type { Task, User, Tag, TaskTemplate } from "../types/types.ts";
import { TaskUpdateDto } from "../hooks/useTodos.ts";
import { MemberSelectionDialog } from "./MemberSelectionDialog.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import Switch from "@mui/material/Switch";
import { isoToYmd, ymdToUtcIso } from "../utils/date.ts";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { RoomStatus } from "../types/types.ts";
import {
  PaletteColorHex,
  PaletteColorDarkHex,
  PaletteColor,
} from "../types/PaletteColor.ts";
import { useDarkMode } from "../hooks/useDarkMode.ts";
import TaskIcon from "@mui/icons-material/Task";
import ListAltIcon from "@mui/icons-material/ListAlt";

// --- Propsの型定義 ---
interface TaskTableViewProps {
  tasks: Task[];
  roomMembers: User[];
  availableTags: Tag[];
  updateTask: (taskId: string, taskData: TaskUpdateDto) => Promise<void>;
  updateTaskStatus: (
    taskId: string,
    newStatus: Task["status"],
  ) => Promise<void>;
  onAddTask: (title: string) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onAddSubtask: (parentTaskId: string, title: string) => Promise<string | null>;
  roomId: number;
  honbuId: number;
  refreshTodos?: () => void;
  roomStatuses: RoomStatus[];
  color: PaletteColor;
  activeListId: string | null;
  onRecurrenceUpdateNeeded?: (info: {
    ruleId: number;
    task: Task;
    isOriginal: boolean;
  }) => void;
}

// --- 編集中セルの型定義 ---
type EditableField =
  | "title"
  | "status"
  | "assignees"
  | "requesters"
  | "dueDate"
  | "priority"
  | "tags";
interface EditingCell {
  taskId: string;
  field: EditableField;
}

// --- 色や表示名の定義 ---
const priorityConfig: { [key in Task["priority"]]: { circleColor: string } } = {
  高: { circleColor: "bg-red-500" },
  中: { circleColor: "bg-orange-500" },
  低: { circleColor: "bg-blue-500" },
  なし: { circleColor: "bg-gray-300" },
};

// --- テーブルの列定義 ---
const columnDefinitions = {
  title: { label: "タスクタイトル", defaultWidth: 450 },
  status: { label: "ステータス", defaultWidth: 120 },
  assignees: { label: "担当者", defaultWidth: 200 },
  requesters: { label: "依頼者", defaultWidth: 200 },
  dueDate: { label: "期限", defaultWidth: 140 },
  priority: { label: "重要度", defaultWidth: 110 },
  tags: { label: "タグ", defaultWidth: 250 },
};
type ColumnKey = keyof typeof columnDefinitions;
const columnOrder = Object.keys(columnDefinitions) as ColumnKey[];

// --- 汎用的なポップアップコンポーネント ---
const Popover: React.FC<{
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}> = ({ onClose, children, className = "" }) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      )
        onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);
  return (
    <div
      ref={popoverRef}
      className={`absolute z-20 top-full mt-1 bg-white shadow-lg rounded-md border p-3 ${className}`}
    >
      {children}
    </div>
  );
};

// --- 日付編集用ポップアップ ---
const DueDatePopover: React.FC<{
  task: Task;
  onSave: (updates: Partial<TaskUpdateDto>) => void;
  onClose: () => void;
}> = ({ task, onSave, onClose }) => {
  const [isUnset, setIsUnset] = useState(!task.startDate);
  const [isRange, setIsRange] = useState(
    !!task.startDate && !!task.endDate && task.startDate !== task.endDate,
  );
  const [startDate, setStartDate] = useState(
    task.startDate ? isoToYmd(task.startDate) : "",
  );
  const [endDate, setEndDate] = useState(
    task.endDate ? isoToYmd(task.endDate) : "",
  );
  const handleSave = useCallback(() => {
    if (isUnset) {
      onSave({ startDate: undefined, endDate: undefined });
    } else {
      const startISO = startDate ? ymdToUtcIso(startDate) : undefined;
      const endISO = isRange && endDate ? ymdToUtcIso(endDate) : startISO;
      onSave({ startDate: startISO, endDate: endISO });
    }
  }, [isUnset, isRange, startDate, endDate, onSave]);
  const handleCloseAndSave = () => {
    handleSave();
    onClose();
  };
  return (
    <Popover onClose={handleCloseAndSave} className="w-64">
      <div className="space-y-3">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm">未設定にする</span>
          <Switch
            size="small"
            checked={isUnset}
            onChange={(e) => setIsUnset(e.target.checked)}
          />
        </label>
        {!isUnset && (
          <>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm">期間を設定</span>
              <Switch
                size="small"
                checked={isRange}
                onChange={(e) => setIsRange(e.target.checked)}
              />
            </label>
            <div>
              <label className="text-xs text-gray-500">
                {isRange ? "開始日" : "日付"}
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-1 border rounded mt-1 text-sm"
              />
            </div>
            {isRange && (
              <div>
                <label className="text-xs text-gray-500">終了日</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full p-1 border rounded mt-1 text-sm"
                />
              </div>
            )}
          </>
        )}
      </div>
    </Popover>
  );
};

// --- タグ編集用ドロップダウン ---
const TagsDropdown: React.FC<{
  availableTags: Tag[];
  currentTags: Tag[];
  onToggle: (tagId: number) => void;
  onClose: () => void;
}> = ({ availableTags, currentTags, onToggle, onClose }) => {
  const currentTagIds = new Set(currentTags.map((t) => t.id));
  return (
    <Popover onClose={onClose} className="w-60">
      <div className="space-y-1 max-h-60 overflow-y-auto">
        {availableTags.map((tag) => {
          const isSelected = currentTagIds.has(tag.id);
          return (
            <div
              key={tag.id}
              onClick={() => onToggle(tag.id)}
              className={`p-2 rounded cursor-pointer text-sm ${
                isSelected ? "" : "text-gray-500"
              }`}
              style={{
                backgroundColor: isSelected ? `${tag.color}20` : "transparent",
              }}
            >
              {tag.name}
            </div>
          );
        })}
      </div>
    </Popover>
  );
};

interface TaskRowProps {
  task: Task;
  level: number;
  visibleColumns: Record<ColumnKey, boolean>;
  rowHeight: number | undefined;
  rowHeights: Record<string, number>;
  showAllRows: boolean;
  availableTags: Tag[];
  editingCell: EditingCell | null;
  setEditingCell: (cell: EditingCell | null) => void;
  onUpdate: (taskId: string, updates: Partial<TaskUpdateDto>) => void;
  onUpdateStatus: (taskId: string, status: Task["status"]) => void;
  onEditMembers: (task: Task, field: "assignees" | "requesters") => void;
  onRowMouseDown: (e: React.MouseEvent, taskId: string) => void;
  openMenuTaskId: string | null;
  setOpenMenuTaskId: (id: string | null) => void;
  onDeleteTask: (taskId: string) => void;
  onAddSubtask: (parentTaskId: string, title: string) => Promise<string | null>;
  roomId: number;
  honbuId: number;
  refreshTodos?: () => void;
  roomStatuses: RoomStatus[];
  color: PaletteColor;
  isDarkMode: boolean;
}

// --- テーブル行を再帰的にレンダリングするコンポーネント ---
const TaskRow: React.FC<TaskRowProps> = ({
  task,
  level,
  visibleColumns,
  rowHeight,
  rowHeights,
  showAllRows,
  availableTags,
  editingCell,
  setEditingCell,
  onUpdate,
  onUpdateStatus,
  onEditMembers,
  onRowMouseDown,
  openMenuTaskId,
  setOpenMenuTaskId,
  onDeleteTask,
  onAddSubtask,
  roomId,
  honbuId,
  refreshTodos,
  roomStatuses,
  color,
  isDarkMode,
}) => {
  const isSubtask = level > 0;
  const isEditing = (field: EditableField) =>
    editingCell?.taskId === task.id && editingCell.field === field;
  const contentClass = showAllRows ? "" : "line-clamp-2";
  const trStyle: React.CSSProperties = {
    height: showAllRows ? "auto" : rowHeight,
    minHeight: showAllRows ? "auto" : "52px",
  };

  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const newSubtaskInputRef = useRef<HTMLInputElement>(null);
  const [subtaskTemplates, setSubtaskTemplates] = useState<TaskTemplate[]>([]);
  const [showSubtaskTemplates, setShowSubtaskTemplates] = useState(false);
  const templateMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [templatePosition, setTemplatePosition] = useState<"top" | "bottom">(
    "top",
  );

  // 確認ダイアログ用のstate
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, message: "", onConfirm: () => {} });

  useEffect(() => {
    if (showSubtaskTemplates && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow > 240 || spaceBelow > spaceAbove) {
        setTemplatePosition("bottom");
      } else {
        setTemplatePosition("top");
      }
    }
  }, [showSubtaskTemplates]);

  const handleCreateSubtask = async () => {
    const trimmedTitle = newSubtaskTitle.trim();
    if (trimmedTitle) {
      await onAddSubtask(task.id, trimmedTitle);
    }
    setIsAddingSubtask(false);
    setNewSubtaskTitle("");
  };

  const handleNewSubtaskKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") handleCreateSubtask();
    else if (e.key === "Escape") {
      setIsAddingSubtask(false);
      setNewSubtaskTitle("");
    }
  };

  const handleApplySubtaskTemplate = async (template: TaskTemplate) => {
    const taskData = JSON.parse(template.taskData || "{}");
    const childTasks = taskData.childTasks || [];
    const tasksToCreate = childTasks.length > 0 ? childTasks : [taskData];

    try {
      for (const subtask of tasksToCreate) {
        const payload = {
          title: subtask.title || "テンプレートから作成",
        };

        const response = await fetch(`/api/tasks/${task.id}/subtasks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error("Failed to create subtask");
        }
      }

      if (refreshTodos) {
        await refreshTodos();
      }
    } catch (error) {
      console.error("Failed to create subtasks from template:", error);
      alert("サブタスクの作成に失敗しました。");
    } finally {
      setShowSubtaskTemplates(false);
      setOpenMenuTaskId(null);
    }
  };

  useEffect(() => {
    if (showSubtaskTemplates) {
      const fetchSubtaskTemplates = async () => {
        try {
          const res = await fetch(
            `/api/templates?roomId=${roomId}&honbuId=${honbuId}`,
          );
          if (res.ok) setSubtaskTemplates(await res.json());
        } catch (error) {
          console.error("Failed to fetch subtask templates:", error);
        }
      };
      fetchSubtaskTemplates();
    }
  }, [showSubtaskTemplates, roomId, honbuId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        templateMenuRef.current &&
        !templateMenuRef.current.contains(event.target as Node)
      ) {
        setShowSubtaskTemplates(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isAddingSubtask) {
      newSubtaskInputRef.current?.focus();
    }
  }, [isAddingSubtask]);

  const handleBlur = (field: keyof TaskUpdateDto, value: any) => {
    onUpdate(task.id, { [field]: value });
    setEditingCell(null);
  };
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: keyof TaskUpdateDto,
  ) => {
    if (e.key === "Enter") handleBlur(field, e.currentTarget.value);
    else if (e.key === "Escape") setEditingCell(null);
  };
  const handleToggleTag = (tagId: number) => {
    const currentTagIds = new Set((task.tags || []).map((t) => t.id));
    if (currentTagIds.has(tagId)) currentTagIds.delete(tagId);
    else currentTagIds.add(tagId);
    onUpdate(task.id, { tagIds: Array.from(currentTagIds) });
  };

  return (
    <>
      {/* 確認ダイアログ */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="タスクの削除"
        message={confirmDialog.message}
        confirmText="削除"
        type="danger"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
        color={color}
      />
      <tr
        data-task-id={task.id}
        className="border-b border-gray-200 bg-white hover:bg-gray-50/50 group relative"
        style={trStyle}
      >
        {columnOrder.map(
          (key) =>
            visibleColumns[key] && (
              <td
                key={key}
                className={`p-2 border-r align-top relative ${
                  isSubtask && (key === "priority" || key === "tags")
                    ? "bg-gray-100"
                    : ""
                } ${
                  (task.completed &&
                    (key === "assignees" || key === "dueDate")) ||
                  isEditing(key)
                    ? ""
                    : "cursor-pointer"
                }`}
                onClick={() => {
                  if (
                    task.completed &&
                    (key === "assignees" || key === "dueDate")
                  ) {
                    return;
                  }
                  if (isEditing(key)) return;
                  if (key === "assignees" || key === "requesters") {
                    onEditMembers(task, key);
                    return;
                  }
                  if (isSubtask && (key === "priority" || key === "tags"))
                    return;
                  setEditingCell({
                    taskId: task.id,
                    field: key as EditableField,
                  });
                }}
              >
                {
                  {
                    title: isEditing("title") ? (
                      <input
                        type="text"
                        defaultValue={task.title}
                        onBlur={(e) => handleBlur("title", e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, "title")}
                        autoFocus
                        className="w-full px-1 py-0.5 border border-blue-400 rounded focus:outline-none"
                      />
                    ) : (
                      (() => {
                        // タスクのステータス色を取得
                        const taskStatusInfo = roomStatuses.find(
                          (s) => s.statusName === task.status,
                        );
                        const taskTitleColor = taskStatusInfo
                          ? taskStatusInfo.color === "Gray"
                            ? isDarkMode
                              ? isSubtask
                                ? "#e5e7eb"
                                : "#f3f4f6"
                              : isSubtask
                                ? "#374151"
                                : "#1f2937"
                            : isDarkMode
                              ? PaletteColorDarkHex[taskStatusInfo.color]
                              : PaletteColorHex[taskStatusInfo.color]
                          : isDarkMode
                            ? isSubtask
                              ? "#e5e7eb"
                              : "#f3f4f6"
                            : isSubtask
                              ? "#374151"
                              : "#1f2937";

                        return (
                          <div className="flex items-center justify-between w-full group">
                            <div
                              style={{
                                paddingLeft: `${level * 24}px`,
                                color: taskTitleColor,
                              }}
                              className={`flex items-center ${contentClass} ${
                                task.completed ? "line-through" : ""
                              }`}
                            >
                              {task.title}
                            </div>
                            <div className="relative">
                              <button
                                ref={menuButtonRef}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenuTaskId(task.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 rounded-full hover:bg-gray-200 text-gray-500"
                              >
                                <MoreVertIcon fontSize="small" />
                              </button>
                              {openMenuTaskId === task.id && (
                                <Popover
                                  onClose={() => setOpenMenuTaskId(null)}
                                  className="w-48 !p-1"
                                >
                                  <div className="text-sm text-gray-700">
                                    {!isSubtask && (
                                      <>
                                        <div
                                          onClick={() => {
                                            setIsAddingSubtask(true);
                                            setOpenMenuTaskId(null);
                                          }}
                                          className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                                        >
                                          サブタスクを追加
                                        </div>
                                        <div
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowSubtaskTemplates(true);
                                          }}
                                          className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                                        >
                                          テンプレートから追加
                                        </div>
                                      </>
                                    )}
                                    <div
                                      onClick={() => {
                                        setOpenMenuTaskId(null);
                                        setConfirmDialog({
                                          isOpen: true,
                                          message: `タスク「${task.title}」を削除しますか？\nこの操作は元に戻せません。`,
                                          onConfirm: () => {
                                            setConfirmDialog((prev) => ({
                                              ...prev,
                                              isOpen: false,
                                            }));
                                            onDeleteTask(task.id);
                                          },
                                        });
                                      }}
                                      className="p-2 hover:bg-red-50 text-red-600 rounded cursor-pointer"
                                    >
                                      削除
                                    </div>
                                  </div>
                                  {showSubtaskTemplates && (
                                    <div
                                      ref={templateMenuRef}
                                      className={`absolute ${
                                        templatePosition === "top"
                                          ? "bottom-0"
                                          : "top-full"
                                      } left-full ml-1 w-60 bg-white border rounded shadow-lg z-20 max-h-60 overflow-y-auto`}
                                    >
                                      {subtaskTemplates.length > 0 ? (
                                        subtaskTemplates.map((template) => {
                                          const taskData = JSON.parse(
                                            template.taskData || "{}",
                                          );
                                          const childTasks =
                                            taskData.childTasks || [];
                                          return (
                                            <button
                                              key={template.templateId}
                                              onClick={() =>
                                                handleApplySubtaskTemplate(
                                                  template,
                                                )
                                              }
                                              className="w-full text-left p-3 text-sm hover:bg-gray-100 flex flex-col gap-1"
                                            >
                                              <div className="flex items-center gap-2">
                                                {/* テンプレートタイプに応じたアイコン */}
                                                {childTasks.length > 0 ? (
                                                  <ListAltIcon
                                                    fontSize="small"
                                                    titleAccess="マルチテンプレート"
                                                  />
                                                ) : (
                                                  <TaskIcon
                                                    fontSize="small"
                                                    titleAccess="シングルテンプレート"
                                                  />
                                                )}
                                                <span className="truncate">
                                                  {template.templateName}
                                                </span>
                                                {/* 共有中の表示 */}
                                                {template.isShare && (
                                                  <span className="px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                                                    共有中
                                                  </span>
                                                )}
                                              </div>
                                              {/* サブタスク数の表示 */}
                                              {childTasks.length > 0 && (
                                                <p className="text-xs text-gray-500">
                                                  {childTasks.length}
                                                  件のサブタスクが作られます
                                                </p>
                                              )}
                                            </button>
                                          );
                                        })
                                      ) : (
                                        <p className="p-3 text-sm text-gray-500">
                                          利用可能なテンプレートはありません。
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </Popover>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    ),
                    status: (() => {
                      const currentStatus = roomStatuses.find(
                        (s) => s.statusName === task.status,
                      );
                      const statusColor = currentStatus
                        ? isDarkMode
                          ? PaletteColorDarkHex[currentStatus.color]
                          : PaletteColorHex[currentStatus.color]
                        : "#cccccc";
                      return (
                        <>
                          <div className="flex items-center justify-center gap-2 h-full">
                            <span
                              className="w-2.5 h-2.5 rounded-full"
                              style={{
                                backgroundColor: statusColor,
                              }}
                            ></span>
                            <span>{task.status}</span>
                          </div>
                          {isEditing("status") && (
                            <Popover
                              onClose={() => setEditingCell(null)}
                              className="w-36 !p-1"
                            >
                              <div>
                                {roomStatuses.map((statusInfo) => (
                                  <div
                                    key={statusInfo.roomStatusId}
                                    onClick={() => {
                                      onUpdateStatus(
                                        task.id,
                                        statusInfo.statusName as Task["status"],
                                      );
                                      setEditingCell(null);
                                    }}
                                    className={`flex items-center gap-2 p-2 cursor-pointer rounded ${
                                      isDarkMode
                                        ? "hover:bg-gray-600"
                                        : "hover:bg-gray-100"
                                    }`}
                                  >
                                    <span
                                      className="w-3 h-3 rounded-full"
                                      style={{
                                        backgroundColor: isDarkMode
                                          ? PaletteColorDarkHex[
                                              statusInfo.color
                                            ]
                                          : PaletteColorHex[statusInfo.color],
                                      }}
                                    ></span>
                                    <span>{statusInfo.statusName}</span>
                                  </div>
                                ))}
                              </div>
                            </Popover>
                          )}
                        </>
                      );
                    })(),
                    assignees:
                      task.completed && task.completedBy ? (
                        <div className="h-full text-sm text-gray-700 flex items-center">
                          {`${task.completedBy.name || "情報なし"}が完了`}
                        </div>
                      ) : (
                        <div className={`h-full ${contentClass}`}>
                          {(task.assignees || []).length > 0 ? (
                            task.assignees.map((a: User) => a.name).join(", ")
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      ),
                    requesters: (
                      <div className={`h-full ${contentClass}`}>
                        {(task.requesters || []).length > 0 ? (
                          task.requesters.map((u) => u.name).join(", ")
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    ),
                    dueDate: (
                      <div className="h-full flex items-center justify-center">
                        {task.completed && task.completedAt ? (
                          <div className="text-sm text-gray-700">
                            完了: {isoToYmd(task.completedAt)}
                          </div>
                        ) : (
                          <>
                            <div>
                              {task.startDate &&
                              task.endDate &&
                              task.startDate !== task.endDate ? (
                                <div className="text-xs flex flex-col items-center">
                                  <span>開始: {isoToYmd(task.startDate)}</span>
                                  <span>終了: {isoToYmd(task.endDate)}</span>
                                </div>
                              ) : task.startDate ? (
                                isoToYmd(task.startDate)
                              ) : (
                                "—"
                              )}
                            </div>
                            {isEditing("dueDate") && (
                              <DueDatePopover
                                task={task}
                                onSave={(updates) => onUpdate(task.id, updates)}
                                onClose={() => setEditingCell(null)}
                              />
                            )}
                          </>
                        )}
                      </div>
                    ),
                    priority: (
                      <>
                        {!isSubtask ? (
                          <>
                            <div className="flex items-center justify-center gap-2 h-full">
                              <span
                                className={`w-2.5 h-2.5 rounded-full ${
                                  priorityConfig[task.priority]?.circleColor ||
                                  "bg-gray-300"
                                }`}
                              ></span>
                              <span>{task.priority}</span>
                            </div>
                            {isEditing("priority") && (
                              <Popover
                                onClose={() => setEditingCell(null)}
                                className="w-36 !p-1"
                              >
                                <div>
                                  {Object.entries(priorityConfig).map(
                                    ([p, c]) => (
                                      <div
                                        key={p}
                                        onClick={() => {
                                          onUpdate(task.id, {
                                            priority: p as Task["priority"],
                                          });
                                          setEditingCell(null);
                                        }}
                                        className="flex items-center gap-2 p-2 hover:bg-gray-100 cursor-pointer rounded"
                                      >
                                        <span
                                          className={`w-3 h-3 rounded-full ${c.circleColor}`}
                                        ></span>
                                        <span>{p}</span>
                                      </div>
                                    ),
                                  )}
                                </div>
                              </Popover>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center justify-center gap-2 h-full text-gray-400">
                            <span
                              className={`w-2.5 h-2.5 rounded-full ${
                                priorityConfig[task.priority]?.circleColor ||
                                "bg-gray-300"
                              }`}
                            ></span>
                            <span>{task.priority}</span>
                          </div>
                        )}
                      </>
                    ),
                    tags: (
                      <>
                        {!isSubtask ? (
                          <>
                            <div
                              className={`flex flex-wrap items-center gap-y-1 h-full ${contentClass}`}
                            >
                              {(task.tags || []).map((t) => (
                                <span
                                  key={t.id}
                                  className="mr-3"
                                  style={{ color: t.color }}
                                >
                                  #{t.name}
                                </span>
                              ))}
                            </div>
                            {isEditing("tags") && (
                              <TagsDropdown
                                availableTags={availableTags}
                                currentTags={task.tags || []}
                                onToggle={handleToggleTag}
                                onClose={() => setEditingCell(null)}
                              />
                            )}
                          </>
                        ) : (
                          <div
                            className={`flex flex-wrap items-center gap-y-1 h-full text-gray-400 ${contentClass}`}
                          >
                            {(task.tags || []).map((t) => (
                              <span key={t.id} className="mr-3">
                                #{t.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    ),
                  }[key]
                }
              </td>
            ),
        )}
        <td className="border-r"></td>
      </tr>
      {(task.childTasks || []).map((subtask) => (
        <TaskRow
          key={subtask.id}
          task={subtask}
          level={level + 1}
          visibleColumns={visibleColumns}
          rowHeights={rowHeights}
          rowHeight={rowHeights[subtask.id]}
          showAllRows={showAllRows}
          availableTags={availableTags}
          editingCell={editingCell}
          setEditingCell={setEditingCell}
          onUpdate={onUpdate}
          onUpdateStatus={onUpdateStatus}
          onEditMembers={onEditMembers}
          onRowMouseDown={onRowMouseDown}
          openMenuTaskId={openMenuTaskId}
          setOpenMenuTaskId={setOpenMenuTaskId}
          onDeleteTask={onDeleteTask}
          onAddSubtask={onAddSubtask}
          roomId={roomId}
          honbuId={honbuId}
          refreshTodos={refreshTodos}
          roomStatuses={roomStatuses}
          color={color}
          isDarkMode={isDarkMode}
        />
      ))}
      {isAddingSubtask && (
        <tr className="border-b border-gray-200 bg-blue-50">
          {columnOrder.map(
            (key) =>
              visibleColumns[key] && (
                <td key={key} className="p-2 border-r align-top">
                  {key === "title" && (
                    <div style={{ paddingLeft: `${(level + 1) * 24}px` }}>
                      <input
                        ref={newSubtaskInputRef}
                        type="text"
                        value={newSubtaskTitle}
                        onChange={(e) => setNewSubtaskTitle(e.target.value)}
                        onKeyDown={handleNewSubtaskKeyDown}
                        onBlur={handleCreateSubtask}
                        placeholder="新規サブタスクを入力 (Enterで確定)"
                        className="w-full px-1 py-0.5 border border-blue-400 rounded focus:outline-none"
                      />
                    </div>
                  )}
                </td>
              ),
          )}
          <td className="border-r"></td>
        </tr>
      )}
    </>
  );
};

// --- メインのテーブルビューコンポーネント ---
export const TaskTableView: React.FC<TaskTableViewProps> = ({
  tasks,
  roomMembers,
  availableTags,
  updateTask,
  updateTaskStatus,
  onAddTask,
  onDeleteTask,
  onAddSubtask,
  roomId,
  honbuId,
  refreshTodos,
  roomStatuses,
  color,
  activeListId,
  onRecurrenceUpdateNeeded,
}) => {
  const isDarkMode = useDarkMode();
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [targetTask, setTargetTask] = useState<Task | null>(null);
  const [memberDialogType, setMemberDialogType] = useState<
    "assignees" | "requesters" | null
  >(null);

  // LocalStorage用のキーを生成
  const storageKey = `table_settings_${honbuId}_${roomId}_${
    activeListId || "none"
  }`;

  const [visibleColumns, setVisibleColumns] = useState<
    Record<ColumnKey, boolean>
  >({
    title: true,
    status: true,
    assignees: roomId !== 0, // RoomID=0の場合は非表示
    requesters: roomId !== 0, // RoomID=0の場合は非表示
    dueDate: true,
    priority: true,
    tags: true,
  });
  const [columnWidths, setColumnWidths] = useState<Record<ColumnKey, number>>(
    () =>
      columnOrder.reduce(
        (acc, key) => ({ ...acc, [key]: columnDefinitions[key].defaultWidth }),
        {} as Record<ColumnKey, number>,
      ),
  );
  const [rowHeights, setRowHeights] = useState<Record<string, number>>({});
  const [showAllRows, setShowAllRows] = useState(false);
  const resizingRef = useRef<{
    type: "col" | "row";
    key: string;
    startPos: number;
    startSize: number;
  } | null>(null);

  const [openMenuTaskId, setOpenMenuTaskId] = useState<string | null>(null);

  const [newTaskTitle, setNewTaskTitle] = useState("");
  const newTaskInputRef = useRef<HTMLInputElement>(null);

  // storageKeyが変更されたら、LocalStorageから設定を再読み込み
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.visibleColumns) {
          setVisibleColumns(parsed.visibleColumns);
        }
      } catch (error) {
        console.error("Failed to load table settings:", error);
      }
    }
  }, [storageKey]);

  // 設定が変更されたらLocalStorageに保存
  useEffect(() => {
    const settings = {
      visibleColumns,
    };
    localStorage.setItem(storageKey, JSON.stringify(settings));
  }, [visibleColumns, storageKey]);

  const handleCreateTask = useCallback(
    async (options?: { refocus?: boolean }) => {
      const trimmedTitle = newTaskTitle.trim();
      if (!trimmedTitle) return;
      await onAddTask(trimmedTitle);
      setNewTaskTitle("");
      if (options?.refocus) {
        requestAnimationFrame(() => {
          newTaskInputRef.current?.focus();
        });
      }
    },
    [newTaskTitle, onAddTask],
  );

  const handleNewTaskKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateTask({ refocus: true });
    }
  };

  const handleToggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const handleMouseDown = (
    e: React.MouseEvent,
    type: "col" | "row",
    key: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = {
      type,
      key,
      startPos: type === "col" ? e.clientX : e.clientY,
      startSize:
        type === "col" ? columnWidths[key as ColumnKey] : rowHeights[key] || 52,
    };
    document.body.style.cursor = type === "col" ? "col-resize" : "row-resize";
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      const { type, key, startPos, startSize } = resizingRef.current;
      if (type === "col") {
        const newWidth = startSize + (e.clientX - startPos);
        if (newWidth > 80)
          setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));
      } else {
        const newHeight = startSize + (e.clientY - startPos);
        if (newHeight > 32)
          setRowHeights((prev) => ({ ...prev, [key]: newHeight }));
      }
    };
    const handleMouseUp = () => {
      resizingRef.current = null;
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const findTaskById = useCallback(
    (sTasks: Task[], id: string): Task | null => {
      for (const t of sTasks) {
        if (t.id === id) return t;
        const f = findTaskById(t.childTasks || [], id);
        if (f) return f;
      }
      return null;
    },
    [],
  );

  const findParentTask = useCallback(
    (sTasks: Task[], childId: string): Task | null => {
      for (const t of sTasks) {
        if ((t.childTasks || []).some((c) => c.id === childId)) return t;
        const found = findParentTask(t.childTasks || [], childId);
        if (found) return found;
      }
      return null;
    },
    [],
  );

  const RECURRENCE_RELEVANT_FIELDS: (keyof TaskUpdateDto)[] = [
    "title",
    "memo",
    "priority",
    "notification",
    "assigneeHonbuIds",
    "requesterHonbuIds",
    "tagIds",
  ];
  const handleUpdate = useCallback(
    async (taskId: string, updates: Partial<TaskUpdateDto>) => {
      const originalTask = findTaskById(tasks, taskId);
      if (!originalTask) return;
      const taskData: TaskUpdateDto = {
        title: originalTask.title,
        tagIds: (originalTask.tags || []).map((t) => t.id),
        startDate: originalTask.startDate,
        endDate: originalTask.endDate,
        memo: originalTask.memo,
        notification: originalTask.notification,
        priority: originalTask.priority,
        type: originalTask.type,
        assigneeHonbuIds: (originalTask.assignees || []).map((u) =>
          parseInt(u.id, 10),
        ),
        requesterHonbuIds: (originalTask.requesters || []).map((u) =>
          parseInt(u.id, 10),
        ),
        ...updates,
      };
      await updateTask(taskId, taskData);

      const hasRelevantChange = RECURRENCE_RELEVANT_FIELDS.some(
        (f) => f in updates,
      );
      if (hasRelevantChange && onRecurrenceUpdateNeeded) {
        const resolvedAssignees = (taskData.assigneeHonbuIds || [])
          .map((id) => roomMembers.find((m) => parseInt(m.id) === id))
          .filter(Boolean) as User[];
        const resolvedRequesters = (taskData.requesterHonbuIds || [])
          .map((id) => roomMembers.find((m) => parseInt(m.id) === id))
          .filter(Boolean) as User[];
        const resolvedTags = (taskData.tagIds || [])
          .map((id) => availableTags.find((t) => t.id === id))
          .filter(Boolean) as Tag[];
        const updatedTask: Task = {
          ...originalTask,
          title: taskData.title,
          memo: taskData.memo ?? "",
          notification: taskData.notification,
          priority: taskData.priority,
          assignees: resolvedAssignees,
          requesters: resolvedRequesters,
          tags: resolvedTags,
        };

        const parentTask = findParentTask(tasks, taskId);

        if (parentTask) {
          // サブタスク編集時: 親タスクの繰り返しルールを確認し、更新された親を渡す
          let ruleId: number | null = parentTask.sourceRecurringRuleId ?? null;
          let isOriginal = false;
          if (!ruleId) {
            if (
              parentTask.recurrenceRules &&
              parentTask.recurrenceRules.length > 0
            ) {
              ruleId = parentTask.recurrenceRules[0].recurringRuleId;
              isOriginal = true;
            } else {
              try {
                const resp = await fetch(
                  `/api/tasks/${parentTask.id}/recurrence-rule`,
                );
                if (resp.ok) {
                  const rule = await resp.json();
                  ruleId = rule?.recurringRuleId ?? null;
                  isOriginal = ruleId != null;
                }
              } catch {}
            }
          }
          if (ruleId) {
            const updatedChildTasks = (parentTask.childTasks || []).map((ct) =>
              ct.id === taskId ? updatedTask : ct,
            );
            onRecurrenceUpdateNeeded({
              ruleId,
              task: { ...parentTask, childTasks: updatedChildTasks },
              isOriginal,
            });
          }
        } else {
          let ruleId = originalTask.sourceRecurringRuleId ?? null;
          let isOriginal = false;
          if (!ruleId) {
            try {
              const resp = await fetch(`/api/tasks/${taskId}/recurrence-rule`);
              if (resp.ok) {
                const rule = await resp.json();
                ruleId = rule?.recurringRuleId ?? null;
                isOriginal = ruleId != null;
              }
            } catch {}
          }
          if (ruleId) {
            onRecurrenceUpdateNeeded({
              ruleId,
              task: updatedTask,
              isOriginal,
            });
          }
        }
      }
    },
    [
      tasks,
      updateTask,
      findTaskById,
      findParentTask,
      onRecurrenceUpdateNeeded,
      roomMembers,
      availableTags,
    ],
  );
  const handleEditMembers = (task: Task, field: "assignees" | "requesters") => {
    setTargetTask(task);
    setMemberDialogType(field);
  };
  const onRowMouseDown = (e: React.MouseEvent, taskId: string) => {
    e.preventDefault();
    handleMouseDown(e, "row", taskId);
  };
  const handleMemberSave = (selectedNames: string[]) => {
    if (!targetTask || !memberDialogType) return;
    const selectedIds = selectedNames
      .map((name) => roomMembers.find((m) => m.name === name)?.id)
      .filter((id): id is string => !!id)
      .map((id) => parseInt(id, 10));
    const fieldKey =
      memberDialogType === "assignees"
        ? "assigneeHonbuIds"
        : "requesterHonbuIds";
    handleUpdate(targetTask.id, { [fieldKey]: selectedIds });
    setTargetTask(null);
    setMemberDialogType(null);
  };

  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      {memberDialogType && targetTask && (
        <MemberSelectionDialog
          roomMembers={roomMembers}
          initialSelected={(memberDialogType === "assignees"
            ? targetTask.assignees
            : targetTask.requesters || []
          ).map((u) => u.name)}
          onSave={handleMemberSave}
          onClose={() => setMemberDialogType(null)}
          dialogType={memberDialogType}
        />
      )}
      <div className="p-2 border-b flex items-center justify-end flex-shrink-0">
        <div className="flex items-center gap-2">
          {columnOrder
            .filter((key) => {
              // RoomID=0の場合は担当者・依頼者列を表示しない
              if (
                roomId === 0 &&
                (key === "assignees" || key === "requesters")
              ) {
                return false;
              }
              return true;
            })
            .map((key) => (
              <button
                key={key}
                onClick={() => handleToggleColumn(key)}
                className={`px-2 py-1 text-xs rounded ${
                  visibleColumns[key]
                    ? "text-white"
                    : "bg-gray-200 text-gray-700"
                }`}
                style={
                  visibleColumns[key]
                    ? { backgroundColor: PaletteColorHex[color] }
                    : {}
                }
              >
                {columnDefinitions[key].label}
              </button>
            ))}
          <button
            onClick={() => setShowAllRows(!showAllRows)}
            className="ml-2 px-2 py-1 text-xs rounded bg-gray-200 text-gray-700"
          >
            {showAllRows ? "高さ固定" : "全行表示"}
          </button>
        </div>
      </div>
      <div
        className="flex-grow overflow-auto"
        onClick={() => setOpenMenuTaskId(null)}
      >
        <table
          className="min-w-full text-sm border-collapse"
          style={{ tableLayout: "fixed" }}
        >
          <colgroup>
            {columnOrder.map(
              (key) =>
                visibleColumns[key] && (
                  <col key={key} style={{ width: `${columnWidths[key]}px` }} />
                ),
            )}
            <col style={{ width: `40px` }} />
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="bg-gray-100 text-gray-600 font-semibold select-none">
              {columnOrder.map(
                (key) =>
                  visibleColumns[key] && (
                    <th
                      key={key}
                      className="p-2 border-b-2 border-r border-gray-200 text-center relative group"
                    >
                      {columnDefinitions[key].label}
                      <div
                        onMouseDown={(e) => handleMouseDown(e, "col", key)}
                        className="absolute top-0 right-[-2px] h-full w-1.5 cursor-col-resize group-hover:bg-blue-300"
                      />
                    </th>
                  ),
              )}
              <th className="p-2 border-b-2 border-r border-gray-200"></th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                level={0}
                visibleColumns={visibleColumns}
                rowHeights={rowHeights}
                rowHeight={rowHeights[task.id]}
                showAllRows={showAllRows}
                availableTags={availableTags}
                editingCell={editingCell}
                setEditingCell={setEditingCell}
                onUpdate={handleUpdate}
                onUpdateStatus={updateTaskStatus}
                onEditMembers={handleEditMembers}
                onRowMouseDown={onRowMouseDown}
                openMenuTaskId={openMenuTaskId}
                setOpenMenuTaskId={setOpenMenuTaskId}
                onDeleteTask={onDeleteTask}
                onAddSubtask={onAddSubtask}
                roomId={roomId}
                honbuId={honbuId}
                refreshTodos={refreshTodos}
                roomStatuses={roomStatuses}
                color={color}
                isDarkMode={isDarkMode}
              />
            ))}

            <tr className="bg-gray-50/50 sticky bottom-0 z-10">
              {columnOrder.map(
                (key, index) =>
                  visibleColumns[key] && (
                    <td
                      key={key}
                      className={`p-1 border-r ${
                        index === 0 ? "border-l" : ""
                      }`}
                    >
                      {key === "title" ? (
                        <input
                          ref={newTaskInputRef}
                          type="text"
                          value={newTaskTitle}
                          onChange={(e) => setNewTaskTitle(e.target.value)}
                          onKeyDown={handleNewTaskKeyDown}
                          onBlur={() => handleCreateTask()}
                          placeholder="＋ タスクを新規作成 (Enterで確定)"
                          className="w-full p-1 bg-transparent outline-none text-sm"
                        />
                      ) : (
                        <div className="h-[26px]"></div>
                      )}
                    </td>
                  ),
              )}
              <td className="border-r"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
